import { Helmet } from "react-helmet-async";
import { SITE_ORIGIN, SITE_NAME } from "./data";

/**
 * Single source of truth for per-page SEO metadata. Every route that we
 * want indexed passes props here.
 *
 * Design intent (targeting page-1 rankings):
 *  * Unique `<title>` per page — Google will demote sites that reuse titles.
 *  * Unique `<meta description>` — never the same on two pages.
 *  * Canonical URL — protects against duplicate-URL penalties from query
 *    string variants (e.g. utm parameters).
 *  * Open Graph + Twitter Card — social crawlers grab these for previews.
 *  * JSON-LD `structured_data` blocks — Google eats these for rich results.
 *
 * Anti-circumvention: `noindex_retailer` prop is set on any page that
 * exposes retailer identity (post-payment reservation pages, retailer
 * dashboards) so those pages never get crawled or indexed.
 */
export default function Seo({
  title,
  description,
  path = "",
  image,
  robots,
  keywords,
  ogType = "website",
  structured_data,
  noindex_retailer = false,
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  const canonical = `${SITE_ORIGIN}${path || "/"}`;
  const fullImage = image || `${SITE_ORIGIN}/og-default.jpg`;
  const robotsValue =
    robots ||
    (noindex_retailer
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1");

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsValue} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Structured data */}
      {structured_data &&
        (Array.isArray(structured_data) ? structured_data : [structured_data]).map((sd, i) => (
          <script key={`${sd?.["@type"] || "ld"}-${i}`} type="application/ld+json">
            {JSON.stringify(sd)}
          </script>
        ))}
    </Helmet>
  );
}

/** Common site-wide JSON-LD nodes reused across pages. */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/logo.png`,
    sameAs: [],
    description:
      "Greater Manchester's marketplace for surplus and remnant flooring, matching consumers to verified independent flooring retailers.",
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_ORIGIN}/#business`,
    name: SITE_NAME,
    url: SITE_ORIGIN,
    image: `${SITE_ORIGIN}/logo.png`,
    priceRange: "££",
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Greater Manchester, United Kingdom",
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Manchester",
      addressRegion: "Greater Manchester",
      addressCountry: "GB",
    },
    description:
      "Reserve surplus flooring from independent retailers across Greater Manchester. Carpet, laminate, LVT and vinyl — matched to your room, collect locally.",
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  };
}
